import { useSWR } from '@tiger/swr';
import { Button, Form, Input, Switch } from 'antd';
import React, { useState } from 'react';
import { $Formutil, withForm } from 'react-antd-formutil';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import BottomBox from 'components/BottomBox';
import CurrencyAmount from 'components/CurrencyAmount';
import ErrorBox from 'components/ErrorBox';
import FieldSelect from 'components/FieldSelect';
import FormItem from 'components/FormItem';
import Loading from 'components/Loading';
import Modal, { useAlertModal } from 'components/Modal';
import ScrollTop from 'components/ScrollTop';
import Toast from 'components/Toast';
import AchBankItem from 'components/vendor/ach/AchBankItem';
import AchSafeVerify from 'components/vendor/ach/AchSafeVerify';
import NoBindAch from 'components/vendor/ach/NoBindAch';
import { PlaidBankCardItem } from 'components/vendor/plaid/PlaidLink/types';
import TaxYearSelect from 'modules/Withdraw/components/TaxYearSelect';
import { openIRAWithdrawLimitsModal } from 'modules/Withdraw/helper';
import { useDistributionTaxYears } from 'modules/Withdraw/hooks';
import iconTip from 'static/images/icons/icon-tip.svg';
import APP from 'stores/APP';
import security from 'stores/security';
import API from 'utils/API';
import { formatMoney } from 'utils/format';
import http from 'utils/http';
import { printf } from 'utils/i18n';
import { getCurrencyInfo } from 'utils/util';
import { withdrawableCash } from './request';
import './style.scss';

const supportCurrency = ['USD'];
const emptyArr = [];

interface FillProps {
    $formutil: $Formutil<{
        amount: string;
        reason: string;
        notes?: string;
        tax_rate?: string;
        tax_year?: number;
    }>;
}

const Fill: React.FC<FillProps> = ({ $formutil }) => {
    const { $invalid, $batchState, $getFirstError, $params, $validate } = $formutil;
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as PlaidBankCardItem | null;
    const { open: openAlertModal } = useAlertModal();
    const [showTax, setShowTax] = useState(false);
    const isIRA = security.isIRAAccount;
    const { isLoading: taxYearListDataLoading } = useDistributionTaxYears();

    const { isLoading, error, data, mutate } = useSWR(
        !!locationState && `withdraw.ach.fill${JSON.stringify(locationState)}`,
        {
            fetcher: () => {
                const { profile_id, id } = locationState!;

                return Promise.all([
                    http
                        .get<{
                            withdraw: {
                                available_withdraw_times: number; // 剩余出金次数
                                five_day: number; // 剩余出金金额
                                once_withdraw: number; // 一次最大出金金额
                            };
                        }>(API.TRADE.funding.availableCash(), {
                            params: {
                                customer_id: security.currentCustomerId,
                                account_id: security.currentAccountId
                            }
                        })
                        .then(({ data }) => data),
                    withdrawableCash({
                        profileId: profile_id,
                        bankCardId: id
                    })
                ]).then(([available, balance]) => ({
                    available: available.withdraw,
                    balance: balance.withdrawable || 0
                }));
            },
            shouldRetryOnError: false,
            revalidateOnFocus: false
        }
    );

    const { data: reasonData = emptyArr } = useSWR<
        Array<{
            reasonDesc: string;
            reasonName: string;
        }>
    >(
        {
            url: API.TRADE.deposit.getWithdrawalReason(),
            params: {
                reason_type: isIRA ? 'ira' : 'individual',
                customer_id: security.currentCustomerId,
                account_id: security.currentAccountId
            }
        },
        {
            dataChain: resp => resp.data
        }
    );

    const [currencyList] = useState(() => {
        return supportCurrency.map(currency => {
            const item = getCurrencyInfo(currency);

            return {
                label: item.name,
                icon: item.icon,
                value: currency
            };
        });
    });

    if (!locationState) {
        return <Navigate to=".." replace />;
    }

    const handleSubmit = async () => {
        const { available } = data!;

        if ($invalid) {
            $batchState({
                $dirty: true,
                $touched: true
            });

            Toast.show($getFirstError());
        } else {
            const amount = Number($params.amount);
            let reasonStr = reasonData.find(item => item.reasonName === $params.reason)?.reasonDesc || '';

            if ($params.notes) {
                reasonStr = `${reasonStr} ${$params.notes}`;
            }

            const params: Record<string, string | number | undefined> = {
                reason: reasonStr,
                tax_year: isIRA ? $params.tax_year : undefined,
                tax_rate: showTax ? Number($params.tax_rate) / 100 : 0
            };

            /**
             * available_withdraw_times: 剩余可出金次数
             * five_day: 剩余可出金金额
             * once_withdraw: 一次最多出金多少
             */
            const { available_withdraw_times, five_day, once_withdraw } = available;
            const reason: string[] = [];

            if (once_withdraw < amount) {
                reason.push(__('Each ACH withdrawal cannot exceed $25,000.'));
            }

            if (!available_withdraw_times) {
                reason.push(__('Each account can initiate at most 4 times of ACH withdrawal per day.'));
            }

            if (amount > five_day) {
                reason.push(
                    __('Each account can initiate at most $50,000 ACH withdrawal in any 5 consecutive business days.')
                );
            }

            if (reason.length > 0) {
                openAlertModal({
                    content: (
                        <>
                            {reason.map(item => (
                                <p className="mb-1" key={item}>
                                    {item}
                                </p>
                            ))}
                        </>
                    ),
                    okText: __('Confirm')
                });

                return;
            }

            Toast.loading(true);

            try {
                const { withdrawable } = await withdrawableCash({
                    profileId: locationState.profile_id,
                    bankCardId: locationState.id
                });

                Toast.loading(false);

                if (withdrawable < amount) {
                    openAlertModal({
                        content: (
                            <div className="text-center">
                                {printf(__('You may withdraw up to %s USD'), withdrawable)}
                            </div>
                        )
                    });

                    return;
                }
            } catch (err) {
                Toast.loading(false);
                Toast.show(err);

                return;
            }

            if (security.optStatus) {
                Modal.open({
                    component: props => <AchSafeVerify {...props} process="fund" />,
                    maskClosable: true,
                    wrapClassName: 'safe-verify-modal',
                    closable: true
                })
                    .result.then(data => {
                        const submitParams = {
                            account_id: locationState.account_id,
                            amount,
                            currency: 'USD',
                            otp_code: data.otp_code,
                            verify_token: data.verify_token
                        };

                        navigate('../confirm', {
                            state: {
                                params: { ...submitParams, ...params },
                                selectedBank: locationState
                            }
                        });
                    })
                    .catch(() => {});
            } else {
                navigate('../confirm', {
                    state: {
                        params: { amount, account_id: locationState.account_id, ...params },
                        selectedBank: locationState
                    }
                });
            }
        }
    };

    const render = () => {
        if (error && !data) {
            // plaid没有匹配到当前银行卡
            if (error.error_code === 403052) {
                return <NoBindAch source="withdraw" onRetry={() => mutate()} />;
            }

            return <ErrorBox error={error} onClick={() => mutate()} />;
        }

        if (isLoading || taxYearListDataLoading) {
            return <Loading />;
        }

        const { balance } = data!;

        return (
            <Form layout="vertical" onFinish={handleSubmit}>
                <TaxYearSelect name="tax_year" />
                <div className="space-box">
                    <p className="label">{__('Transfer Funds To')}</p>
                    <AchBankItem info={locationState} withBorder />
                </div>
                <div className="space-box">
                    <p className="label">{isIRA ? __('Distribution Amount') : __('Withdrawal Amount')}</p>
                    <CurrencyAmount
                        currencyList={currencyList}
                        inputConfig={{
                            name: 'amount',
                            placeholder: '0.00',
                            checker: value => {
                                if (!value) {
                                    return __('Please enter an amount');
                                }

                                if (value <= 0) {
                                    return __('The amount you entered is invalid');
                                }

                                if (!balance || value > balance) {
                                    return __(
                                        'Please lower your withdrawal amount or try with a different bank account'
                                    );
                                }

                                const taxRate = $formutil!.$params.tax_rate;

                                if (taxRate !== undefined && Number(taxRate) * Number(value) < 1) {
                                    return __('Please enter a valid amount.');
                                }

                                return true;
                            }
                        }}
                        selectConfig={{
                            disabled: currencyList.length <= 1,
                            name: 'currency',
                            $defaultValue: 'USD',
                            showIcon: false
                        }}
                    />
                </div>
                <p className="balance">
                    {printf(
                        isIRA ? __('%s is available to distribute') : __('%s is available to withdraw'),
                        formatMoney(balance, '$')
                    )}
                </p>
                {isIRA && (
                    <div className="tax-label" style={{ paddingBottom: 8 }}>
                        <div>{__('Withholding tax')}</div>
                        <Switch checked={showTax} onChange={setShowTax} />
                    </div>
                )}
                {isIRA && showTax && (
                    <FormItem
                        name="tax_rate"
                        className="tax-item"
                        $parser={(value, setValue) => {
                            const num = parseInt(value, 10);
                            const intValue = isNaN(num) ? '' : String(num);

                            setValue(intValue);

                            return intValue;
                        }}
                        $onFieldChange={() => {
                            $formutil.$validate('amount');
                        }}
                        checker={value => {
                            if (typeof value !== 'string' || value.length === 0) {
                                return __('Please enter 10-100, or turn off the switch');
                            }

                            if (value.indexOf('.') !== -1) {
                                return __('Please enter an integer');
                            }

                            if (Number(value) < 10 || Number(value) > 100) {
                                return __('Please enter 10-100, or turn off the switch');
                            }

                            return true;
                        }}>
                        <Input placeholder={__('Please enter 10-100')} suffix="%" />
                    </FormItem>
                )}
                <FormItem
                    name="reason"
                    validMessage={{
                        required: isIRA
                            ? __('Please select distribution reason')
                            : __('Please select withdrawal reason')
                    }}
                    required
                    label={isIRA ? __('Purpose of Distribution') : __('Purpose of Withdrawal')}
                    $onFieldChange={() => {
                        $validate('notes');
                    }}>
                    <FieldSelect
                        placeholder={
                            isIRA ? __('Please select distribution reason') : __('Please select withdrawal reason')
                        }
                        data={reasonData.map(item => ({ value: item.reasonName, label: item.reasonDesc }))}
                    />
                </FormItem>
                <FormItem
                    label={__('Notes')}
                    name="notes"
                    checker={value => {
                        if ($params.reason === '6' && !value) {
                            return isIRA
                                ? __('Please specify your purpose of distribution in Notes.')
                                : __('Please specify your purpose of withdrawal in Notes.');
                        }

                        return true;
                    }}>
                    <Input placeholder={__('Please enter your notes')} />
                </FormItem>
                <BottomBox>
                    <Button htmlType="submit" className="btn-important" type="primary" block>
                        {__('Continue')}
                    </Button>
                    {APP.isTiger && (
                        <p className="policy-text">TradeUP Securities, Inc. is a member of FINRA and SIPC.</p>
                    )}
                </BottomBox>
            </Form>
        );
    };

    return (
        <div className="withdraw-ach-fill-root">
            <ScrollTop />
            <h3 className="main-title">
                {isIRA ? __('ACH Distribution') : __('ACH Withdrawal')}
                {isIRA && <img src={iconTip} alt="tip" onClick={() => openIRAWithdrawLimitsModal()} />}
            </h3>
            {render()}
        </div>
    );
};

export default withForm(Fill);
